import {
  Box,
  Button,
  Flex,
  Text,
  Input,
  Image,
  GridItem,
  SimpleGrid,
  Heading,
  IconButton,
  Skeleton,
  Checkbox,
  Tag,
  TagLabel,
  TagCloseButton,
} from "@chakra-ui/react";
import { DateRangePicker } from "react-date-range";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { FetchROASReport, ResetROASData } from "./action";
import { RootReducer } from "../../../RootReducer";
import { columnsDataComplex } from "./variables/columnsData";
import ComplexTable from "./components/ComplexTable";
import { Select } from "chakra-react-select";
import { FetchOverviewReport } from "../default/action";
import { IoRemoveCircle } from "react-icons/io5";
import { FetchCountryOptions } from "../campaign_create/action";
import { useUserPermissions } from "hooks/useUserPermissions";
import { CloseIcon } from "@chakra-ui/icons";
import { useFilterStore } from "store/filtersStore";
import { CSVLink } from "react-csv";
import GamelightButton from "components/button";

const useOutsideClick = (callback: (ranges: any) => void, ranges: any) => {
  const ref = useRef<any>();

  useEffect(() => {
    const handleClick = (event: any) => {
      if (ref.current && !ref.current.contains(event.target)) {
        callback(ranges);
      }
    };

    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, [ref, ranges]);

  return ref;
};

const testGroupOptions = [
  { label: "0", value: "0" },
  { label: "1", value: "1" },
  { label: "2", value: "2" },
  { label: "3", value: "3" },
];

const genderOptions = [
  { label: "Male", value: "1" },
  { label: "Female", value: "2" },
];

const platformOptions = [
  { label: "Android", value: "1" },
  { label: "iOS", value: "2" },
];

const userSourceOptions = [
  {
    label: "Organic",
    value: "organic",
  },
  {
    label: "Referral",
    value: "Referral",
  },
  {
    label: "Microfluencer",
    value: "Microfluencer",
  },
  {
    label: "Google Ads",
    value: "Google Ads",
  },
  {
    label: "Motive Interactive",
    value: "Motive Interactive",
  },
  {
    label: "Motive Interactive Secondary",
    value: "Motive Interactive Secondary",
  },
  {
    label: "Unity Ads",
    value: "Unity Ads",
  },
  {
    label: "Gamelight",
    value: "Gamelight",
  },
  {
    label: "Facebook",
    value: "Facebook",
  },
  {
    label: "Snapchat",
    value: "Snapchat",
  },
  {
    label: "TikTok",
    value: "TikTok For Business",
  },
  {
    label: "MyAppFree",
    value: "MyAppFree",
  },
  {
    label: "ironSource",
    value: "ironSource",
  },
  {
    label: "ironSource Aura",
    value: "ironSource Aura",
  },
  {
    label: "Tapjoy",
    value: "Tapjoy",
  },
  {
    label: "AppLabs",
    value: "AppLabs",
  },
  {
    label: "AdAction Interactive CPE",
    value: "AdAction Interactive CPE",
  },
  {
    label: "Appluck",
    value: "Appluck",
  },
  {
    label: "Apple Search Ads",
    value: "Apple Search Ads",
  },
  {
    label: "Applovin",
    value: "Applovin",
  },
  {
    label: "Digital Turbine",
    value: "Digital Turbine",
  },
  {
    label: "Moloco",
    value: "Moloco",
  },
  {
    label: "Prodege",
    value: "Prodege",
  },
  {
    label: "Skyflag",
    value: "Skyflag",
  },
  {
    label: "Hang My Ads",
    value: "Hang My Ads",
  },
  {
    label: "Zucks Affiliate",
    value: "Zucks Affiliate",
  },
  {
    label: "Zucks CPE",
    value: "Zucks CPE",
  },
  {
    label: "Liftoff",
    value: "Liftoff",
  },
];

const periodicityOptions = [
  {
    label: "Daily",
    value: "daily",
  },
  {
    label: "Weekly",
    value: "weekly",
  },
  {
    label: "Monthly",
    value: "monthly",
  },
  {
    label: "Total",
    value: "total",
  },
];

const clientOptions = [
  {
    label: "PLAYTIME",
    value: "pro.cashtree",
  },
  {
    label: "Supreme King Android",
    value: "io.purrcat",
  },
  {
    label: "MONEY CASH",
    value: "io.playtime",
  },
  {
    label: "CASH BUDDY",
    value: "io.gametime",
  },
  {
    label: "TM Android",
    value: "gametester.io",
  },
  {
    label: "TM iOS",
    value: "app.testmax",
  },
  {
    label: "Tester Buddy",
    value: "io.buddy",
  },
  {
    label: "Supreme King iOS",
    value: "com.bird.mon",
  },
  {
    label: "PLAYOUT",
    value: "io.playout",
  },
];

interface CoinFilterOption {
  label: string;
  value:
    | "usage"
    | "challenge_open"
    | "challenge_app_event"
    | "challenge_app_revenue_event";
}

const coinFilterOptions: CoinFilterOption[] = [
  { label: "Usage", value: "usage" },
  { label: "Challenge Open", value: "challenge_open" },
  { label: "Challenge App Event", value: "challenge_app_event" },
  {
    label: "Challenge App Revenue Event",
    value: "challenge_app_revenue_event",
  },
];

const isDxHeader = (header: string) => /^D\d+$/.test(header);

const formatPeriodicity = (p: any): string => {
  if (!p) return "";
  switch (p.type) {
    case "daily":
      return p.day;
    case "weekly":
      return `Week ${p.week} ${p.year}`;
    case "monthly":
      return `${p.month}-${p.year}`;
    default:
      return "Total";
  }
};

export default function ROAS() {
  const { isMain } = useUserPermissions();

  const breakdownOptions = isMain
    ? [
        { label: "Campaign ID", value: "partner_campaign_id" },
        { label: "Campaign Name", value: "partner_campaign_name" },
        { label: "Partner App", value: "partner_app" },
        { label: "Test Group", value: "test_group" },
        { label: "Gender", value: "user_gender_id" },
        { label: "Country", value: "user_country_code" },
        { label: "Platform", value: "user_platform_id" },
        { label: "OS Version", value: "user_api_level" },
        { label: "Network", value: "user_source" },
        { label: "UA Campaign ID", value: "user_campaign_id" },
        { label: "UA Campaign Name", value: "user_campaign_name" },
        { label: "Source App", value: "user_app_id" },
        { label: "Install Sequence", value: "install_sequence" },
      ]
    : [
        { label: "Campaign ID", value: "partner_campaign_id" },
        { label: "Campaign Name", value: "partner_campaign_name" },
      ];

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(FetchCountryOptions());
  }, [dispatch]);

  const reportRequestState = useSelector(
    (state: RootReducer) => state.roas.fetchROASReportRequestState
  );
  const report = useSelector((state: RootReducer) => state.roas.report);

  const overviewReport = useSelector(
    (state: RootReducer) => state.overview.report
  );

  const countryOptions = useSelector(
    (state: RootReducer) => state.campaignDetails.countryOptions
  );

  const campaigns = overviewReport ? overviewReport.campaigns : [];

  const {
    selectedApp,
    selectedCampaign,
    selectedTestGroup,
    selectedGender,
    selectedPlatform,
    selectedUserSource,
    selectedClient,
    selectedCountry,
    selectedCoinFilter,
    selectedUserApiLevel,
    selectedInstallSequence,
    periodicity,
    breakdowns,
    setFilters,
    internalRanges,
    userInternalRanges,
  } = useFilterStore();

  const updateFilter = (key: string, value: any) => {
    setFilters({ [key]: value });
  };

  const [installSequenceInput, setInstallSequenceInput] = useState("");
  const [osVersionInput, setOsVersionInput] = useState("");

  const availableBreakdowns = useMemo(() => {
    return breakdownOptions.filter(
      (opt) => !breakdowns.find((brk) => brk?.value === opt?.value)
    );
  }, [breakdowns]);

  const [tableColumns, setTableColumns] = useState(columnsDataComplex);
  const [showCoinsEarned, setShowCoinsEarned] = useState(false);
  const [showArpu, setShowArpu] = useState(false);

  let appOptions: any = [];
  let campaignOptions: any = [];

  campaigns.forEach((c) => {
    appOptions.push({
      label: c.appName,
      value: c.appId,
    });
  });

  appOptions = [
    ...new Map(appOptions.map((item: any) => [item["value"], item])).values(),
  ];

  campaigns.forEach((c) => {
    if (selectedApp && selectedApp.value === c.appId) {
      campaignOptions.push({
        label: c.name,
        value: c.id,
      });
    }
  });

  useEffect(() => {
    if (!selectedApp && appOptions && appOptions.length > 0) {
      updateFilter("selectedApp", appOptions[0]);
    }
  }, [appOptions, selectedApp]);

  useEffect(() => {
    dispatch(
      FetchOverviewReport(
        internalRanges.startDate,
        internalRanges.endDate,
        "all"
      )
    );
  }, [dispatch, internalRanges]);

  const [isDatePickerVisible, setDatePickerVisible] = useState(false);
  const [isUserDatePickerVisible, setUserDatePickerVisible] = useState(false);

  const handleClickOutside = useCallback(() => {
    if (isDatePickerVisible) {
      setDatePickerVisible(false);
    }
  }, [isDatePickerVisible]);

  const handleClickOutsideUserPicker = useCallback(() => {
    if (isUserDatePickerVisible) {
      setUserDatePickerVisible(false);
    }
  }, [isUserDatePickerVisible]);

  const ref = useOutsideClick(handleClickOutside, internalRanges);

  const userDatePickerRef = useOutsideClick(
    handleClickOutsideUserPicker,
    userInternalRanges
  );

  const differentDates =
    internalRanges.startDate.toDateString() ==
    internalRanges.endDate.toDateString()
      ? 1
      : 2;
  const formattedDate =
    differentDates === 1
      ? moment(internalRanges.startDate).format("ll")
      : `${moment(internalRanges.startDate).format("ll")} - ${moment(
          internalRanges.endDate
        ).format("ll")}`;

  const differentUserDates =
    userInternalRanges.startDate == userInternalRanges.endDate ? 1 : 2;
  let formattedUserDate =
    userInternalRanges.startDate && userInternalRanges.startDate
      ? differentUserDates === 1
        ? moment(userInternalRanges.startDate).format("ll")
        : `${moment(userInternalRanges.startDate).format("ll")} - ${moment(
            userInternalRanges.endDate
          ).format("ll")}`
      : null;

  const selectedC = campaigns.find((c) => {
    if (!selectedApp) {
      return;
    }

    return c.appId === selectedApp.value;
  });

  const addBreakdown = (
    index: number,
    value: {
      value: string;
      label: string;
    }
  ) => {
    const newBreakdowns = [...breakdowns];

    if (breakdowns.length === 1 && !value) {
      newBreakdowns[0] = {
        label: undefined,
        value: undefined,
      };

      updateFilter("breakdowns", newBreakdowns);

      return;
    }

    newBreakdowns[index] = value;

    if (
      index === breakdowns.length - 1 &&
      value.value &&
      availableBreakdowns?.length > 1
    ) {
      newBreakdowns.push({ label: "", value: "" });
    }

    updateFilter("breakdowns", newBreakdowns);
  };

  const getPeriodicityColumnConfiguration = () => {
    let config = {
      Header: "Date Range",
      accessor: "periodicity.type",
    };

    switch (periodicity.value) {
      case "daily":
        return { ...config, accessor: "periodicity.day" };

      case "weekly":
        return { ...config, accessor: "periodicity.week" };

      default:
        return config;
    }
  };

  const updateColumnsForCoinsEarned = (showCoinsEarnedValue: boolean) => {
    //@ts-ignore
    setTableColumns((prevTableColumns) => {
      const updatedTableColumns = prevTableColumns.map((column) => {
        switch (column.Header) {
          case "D0":
          case "D1":
          case "D3":
          case "D7":
          case "D14":
          case "D30":
          case "D60":
          case "D90":
          case "D120":
          case "D150":
          case "D180":
          case "D365":
            return showCoinsEarnedValue
              ? {
                  ...column,
                  accessor: (row: any) => {
                    const roas = row.days?.[column.Header]?.roas ?? undefined;
                    const coinsEarned =
                      row.days?.[column.Header]?.coinsEarned ?? undefined;
                    return [roas, coinsEarned];
                  },
                }
              : {
                  ...column,
                  accessor: `days.${column.Header}.roas`,
                };
          default:
            return column;
        }
      });

      return updatedTableColumns;
    });
  };

  const handleApply = () => {
    setTableColumns(
      periodicity
        ? [
            getPeriodicityColumnConfiguration(),
            ...breakdowns
              ?.filter((itm) => itm && itm.value)
              .map((itm) => {
                if (!itm.value) return;

                return {
                  Header: itm.label
                    .split(" ")
                    .map((str: string) => str[0])
                    .join(""),
                  accessor: "breakdowns." + itm.value,
                  hint: itm.label,
                  isBreakdown: true,
                };
              }),
            ...columnsDataComplex,
          ]
        : [
            ...breakdowns
              ?.filter((itm) => itm && itm.value)
              .map((itm) => {
                if (!itm.value) return;

                return {
                  Header: itm.label
                    .split(" ")
                    .map((str: string) => str[0])
                    .join(""),
                  accessor: "breakdowns." + itm.value,
                  hint: itm.label,
                  isBreakdown: true,
                };
              }),
            ...columnsDataComplex,
          ]
    );

    if (showCoinsEarned) {
      updateColumnsForCoinsEarned(true);
    }

    if (!selectedApp) {
      return;
    }

    const testGroup = selectedTestGroup
      ? Array.isArray(selectedTestGroup)
        ? selectedTestGroup.map((itm) => itm.value)
        : selectedTestGroup.value
      : null;

    const gender = selectedGender
      ? Array.isArray(selectedGender)
        ? selectedGender.map((itm) => itm.value)
        : selectedGender.value
      : null;

    const platform = selectedPlatform
      ? Array.isArray(selectedPlatform)
        ? selectedPlatform.map((itm) => itm.value)
        : selectedPlatform.value
      : null;

    const client = selectedClient
      ? Array.isArray(selectedClient)
        ? selectedClient.map((itm) => itm.value)
        : selectedClient.value
      : null;

    const userSource = selectedUserSource
      ? Array.isArray(selectedUserSource)
        ? selectedUserSource.map((itm) => itm.value)
        : selectedUserSource.value
      : null;

    const selectedCountryCode = selectedCountry
      ? Array.isArray(selectedCountry)
        ? selectedCountry.map((itm) => itm.value)
        : selectedCountry.value
      : null;
    const coinsFilters = selectedCoinFilter
      ? {
          changeType: selectedCoinFilter,
        }
      : null;
    const userApiLevel =
      selectedUserApiLevel.length > 0
        ? selectedUserApiLevel.map((v) => parseInt(v, 10))
        : null;

    const installSequence =
      selectedInstallSequence.length > 0
        ? selectedInstallSequence.map((v) => parseInt(v, 10))
        : null;

    const filters = {
      partner_campaign_id: selectedCampaign ? selectedCampaign.value : null,
      partner_app: selectedApp ? selectedApp.value : null,
      "user_id % 4": testGroup,
      user_gender_id: gender,
      user_platform_id: platform,
      user_source: userSource,
      user_app_id: client,
      user_country_code: selectedCountryCode,
      user_api_level: userApiLevel,
      install_sequence: installSequence,
    };

    const breakdownValues = breakdowns
      ?.filter((b) => b && b.value)
      .map((b) => b.value);
    const userFrom =
      isMain && userInternalRanges.startDate
        ? differentUserDates === 2
          ? moment(userInternalRanges.startDate).format("YYYY-MM-DD")
          : null
        : null;
    const userUntil =
      isMain && userInternalRanges.endDate
        ? moment(userInternalRanges.endDate).format("YYYY-MM-DD")
        : null;

    dispatch(
      FetchROASReport({
        from: internalRanges.startDate,
        until: internalRanges.endDate,
        filters,
        breakdowns: breakdownValues,
        periodicity: periodicity?.value as "daily" | "weekly" | "monthly",
        userFrom: userFrom,
        userUntil: userUntil,
        coinsFilters,
      })
    );
  };

  useEffect(() => {
    if (report && report.rows) {
      const sortedRows = [...report.rows];

      if (
        //@ts-ignore
        sortedRows?.[0]?.periodicity.type === "total" &&
        breakdowns?.filter((brk) => brk && brk.value).length === 1
      ) {
        sortedRows.sort((a, b) => b.spend - a.spend);
      } else {
        sortedRows.sort((a, b) => {
          let periodicityComparison = 0;

          //@ts-ignore
          switch (a.periodicity.type) {
            case "daily":
              //@ts-ignore
              const dateA = new Date(a.periodicity.day);
              //@ts-ignore
              const dateB = new Date(b.periodicity.day);
              //@ts-ignore
              periodicityComparison = dateB - dateA;
              break;
            case "weekly":
              periodicityComparison =
                //@ts-ignore
                b.periodicity.year - a.periodicity.year ||
                //@ts-ignore
                b.periodicity.week - a.periodicity.week;
              break;
            case "monthly":
              periodicityComparison =
                //@ts-ignore
                b.periodicity.year - a.periodicity.year ||
                //@ts-ignore
                b.periodicity.month - a.periodicity.month;
              break;
            default:
              break;
          }

          if (periodicityComparison !== 0) {
            return periodicityComparison;
          }

          if (breakdowns && breakdowns?.length > 0 && breakdowns?.[0]?.value) {
            for (const breakdown of breakdowns) {
              const breakdownKey = breakdown.value;
              //@ts-ignore
              if (a.breakdowns[breakdownKey] < b.breakdowns[breakdownKey])
                return -1;
              //@ts-ignore
              if (a.breakdowns[breakdownKey] > b.breakdowns[breakdownKey])
                return 1;
            }
          }

          return 0;
        });
      }

      report.rows = sortedRows;
    }
  }, [report, breakdowns]);

  useEffect(() => {
    return () => {
      dispatch(ResetROASData());
    };
  }, [dispatch]);

  useEffect(() => {
    handleApply();
  }, []);

  const { csvHeaders, csvData } = useMemo(() => {
    const rows: any[] = report?.rows ?? [];
    const cols: any[] = tableColumns.filter(Boolean);

    const headers: { label: string; key: string }[] = [];

    cols.forEach((col) => {
      const h = String(col.Header);
      if (isDxHeader(h)) {
        headers.push({ label: `${h} ROAS (%)`, key: `${h}_roas` });
        headers.push({ label: `${h} ARPU ($)`, key: `${h}_arpu` });
      } else if (h === "Date Range") {
        headers.push({ label: "Date Range", key: "date_range" });
      } else if (col.isBreakdown) {
        headers.push({ label: col.hint || h, key: `bd_${col.accessor}` });
      } else if (h === "SPEND") {
        headers.push({ label: "Spend ($)", key: "spend" });
      } else if (h === "INSTALLS") {
        headers.push({ label: "Installs", key: "installs" });
      } else if (h === "CPI") {
        headers.push({ label: "CPI ($)", key: "cpi" });
      }
    });

    const data = rows.map((row) => {
      const out: { [key: string]: any } = {};
      const spend = Number(row.spend ?? 0);
      const installs = Number(row.installs ?? 0);

      cols.forEach((col) => {
        const h = String(col.Header);
        if (isDxHeader(h)) {
          const roas = row.days?.[h]?.roas;
          const hasRoas = roas != null && !isNaN(roas);
          out[`${h}_roas`] = hasRoas ? (roas * 100).toFixed(2) : "";
          out[`${h}_arpu`] =
            hasRoas && installs > 0
              ? ((roas * spend) / installs).toFixed(3)
              : "";
        } else if (h === "Date Range") {
          out.date_range = formatPeriodicity(row.periodicity);
        } else if (col.isBreakdown) {
          const key = String(col.accessor).replace("breakdowns.", "");
          const val = row.breakdowns?.[key];
          out[`bd_${col.accessor}`] =
            col.accessor === "breakdowns.user_platform_id"
              ? val === 1
                ? "Android"
                : val === 2
                ? "iOS"
                : val
              : val;
        } else if (h === "SPEND") {
          out.spend = spend.toFixed(2);
        } else if (h === "INSTALLS") {
          out.installs = installs;
        } else if (h === "CPI") {
          out.cpi = installs > 0 ? (spend / installs).toFixed(2) : "";
        }
      });

      return out;
    });

    return { csvHeaders: headers, csvData: data };
  }, [report, tableColumns]);

  const csvFileName = `Gamelight_Analytics_${moment(
    internalRanges.startDate
  ).format("YYYY-MM-DD")}_${moment(internalRanges.endDate).format(
    "YYYY-MM-DD"
  )}.csv`;

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Flex alignItems={"flex-start"}>
        <Flex flex={1}>
          {selectedC?.iconUrl ? (
            <Image
              referrerPolicy="no-referrer"
              height={140}
              width={140}
              borderRadius={4}
              marginRight={4}
              src={selectedC.iconUrl}
            />
          ) : (
            <Flex
              bgColor={"gray.200"}
              height={140}
              width={140}
              borderRadius={4}
              marginRight={4}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <Skeleton
                height={140}
                width={140}
                borderRadius={4}
                pos="absolute"
                zIndex={1}
                opacity={0.1}
              />
              <Text
                color={"gray.700"}
                fontSize={"xs"}
                fontWeight="medium"
                zIndex={100}
                textAlign={"center"}
                mx={2}
              >
                Please select an app and a campaign.
              </Text>
            </Flex>
          )}

          <Flex flexDirection={"column"} flex={1}>
            <SimpleGrid columns={2} gap={4} flex={1}>
              <GridItem
                backgroundColor={"white"}
                height="fit-content"
                borderRadius={8}
              >
                <Select
                  value={selectedApp}
                  isMulti={false}
                  placeholder={"Select an App"}
                  onChange={(v) => {
                    updateFilter("selectedApp", v);
                    updateFilter("selectedCampaign", null);
                  }}
                  isClearable={false}
                  //@ts-ignore
                  options={appOptions}
                />
              </GridItem>
              <GridItem
                backgroundColor={"white"}
                height="fit-content"
                borderRadius={8}
              >
                <Select
                  value={selectedCampaign}
                  isMulti={false}
                  placeholder={"Campaign"}
                  onChange={(v) => {
                    updateFilter("selectedCampaign", v);
                  }}
                  isClearable={true}
                  //@ts-ignore
                  options={campaignOptions}
                />
              </GridItem>
              {isMain && (
                <>
                  <GridItem
                    backgroundColor={"white"}
                    height="fit-content"
                    borderRadius={8}
                  >
                    <Select
                      value={selectedTestGroup}
                      isMulti={true}
                      placeholder={"Test Group"}
                      onChange={(v) => {
                        updateFilter("selectedTestGroup", v);
                      }}
                      isClearable={true}
                      //@ts-ignore
                      options={testGroupOptions}
                    />
                  </GridItem>
                  <GridItem
                    backgroundColor={"white"}
                    height="fit-content"
                    borderRadius={8}
                  >
                    <Select
                      value={selectedGender}
                      isMulti={true}
                      placeholder={"Gender"}
                      onChange={(v) => {
                        updateFilter("selectedGender", v);
                      }}
                      isClearable={true}
                      //@ts-ignore
                      options={genderOptions}
                    />
                  </GridItem>
                  <GridItem
                    backgroundColor={"white"}
                    height="fit-content"
                    borderRadius={8}
                  >
                    <Select
                      value={selectedPlatform}
                      isMulti={true}
                      placeholder={"Platform"}
                      onChange={(v) => {
                        updateFilter("selectedPlatform", v);
                      }}
                      isClearable={true}
                      //@ts-ignore
                      options={platformOptions}
                    />
                  </GridItem>
                  <GridItem
                    backgroundColor={"white"}
                    height="fit-content"
                    borderRadius={8}
                  >
                    <Select
                      value={selectedClient}
                      isMulti={true}
                      placeholder={"Source App"}
                      onChange={(v) => {
                        updateFilter("selectedClient", v);
                      }}
                      isClearable={true}
                      //@ts-ignore
                      options={clientOptions}
                    />
                  </GridItem>
                  <GridItem
                    backgroundColor={"white"}
                    height="fit-content"
                    borderRadius={8}
                  >
                    <Select
                      value={selectedUserSource}
                      isMulti={true}
                      placeholder={"Network"}
                      onChange={(v) => {
                        updateFilter("selectedUserSource", v);
                      }}
                      isClearable={true}
                      //@ts-ignore
                      options={userSourceOptions}
                    />
                  </GridItem>
                  <GridItem
                    backgroundColor={"white"}
                    height="fit-content"
                    borderRadius={8}
                  >
                    <Select
                      value={selectedCountry}
                      isMulti={true}
                      placeholder={"Country"}
                      onChange={(v) => {
                        updateFilter("selectedCountry", v);
                      }}
                      isClearable={true}
                      //@ts-ignore
                      options={countryOptions}
                    />
                  </GridItem>

                  <GridItem
                    height="fit-content"
                    borderRadius={8}
                  >
                    <Flex
                      backgroundColor={"white"}
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius={8}
                      px={2}
                      py={1}
                      minH="40px"
                      flexWrap="wrap"
                      alignItems="center"
                      gap={1}
                    >
                      {selectedUserApiLevel.map((val) => (
                        <Tag key={val} size="sm" colorScheme="brand" variant="solid">
                          <TagLabel>{val}</TagLabel>
                          <TagCloseButton
                            onClick={() =>
                              updateFilter(
                                "selectedUserApiLevel",
                                selectedUserApiLevel.filter((v) => v !== val)
                              )
                            }
                          />
                        </Tag>
                      ))}
                      <Input
                        variant="unstyled"
                        placeholder={
                          selectedUserApiLevel.length === 0
                            ? "OS Version"
                            : ""
                        }
                        size="sm"
                        flex={1}
                        minW="80px"
                        value={osVersionInput}
                        onChange={(e) => setOsVersionInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (
                            e.key === "Backspace" &&
                            osVersionInput === "" &&
                            selectedUserApiLevel.length > 0
                          ) {
                            updateFilter(
                              "selectedUserApiLevel",
                              selectedUserApiLevel.slice(0, -1)
                            );
                          }
                          if (e.key === "Enter" || e.key === ",") {
                            e.preventDefault();
                            const raw = osVersionInput
                              .split(",")
                              .map((s) => s.trim())
                              .filter(Boolean);
                            const valid = raw
                              .filter((s) => {
                                const n = Number(s);
                                return !isNaN(n) && n > 0;
                              })
                              .map((s) => String(parseInt(s, 10)));
                            if (valid.length > 0) {
                              const deduped = [
                                ...new Set([
                                  ...selectedUserApiLevel,
                                  ...valid,
                                ]),
                              ];
                              updateFilter("selectedUserApiLevel", deduped);
                            }
                            setOsVersionInput("");
                          }
                        }}
                      />
                    </Flex>
                    <Text fontSize="xs" color="gray.500" mt={1} px={2}>
                      Supports multiple entries separated by comma (e.g. 36, 18.1)
                    </Text>
                  </GridItem>

                  <GridItem
                    height="fit-content"
                    borderRadius={8}
                  >
                    <Flex
                      backgroundColor={"white"}
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius={8}
                      px={2}
                      py={1}
                      minH="40px"
                      flexWrap="wrap"
                      alignItems="center"
                      gap={1}
                    >
                      {selectedInstallSequence.map((val) => (
                        <Tag key={val} size="sm" colorScheme="brand" variant="solid">
                          <TagLabel>{val}</TagLabel>
                          <TagCloseButton
                            onClick={() =>
                              updateFilter(
                                "selectedInstallSequence",
                                selectedInstallSequence.filter((v) => v !== val)
                              )
                            }
                          />
                        </Tag>
                      ))}
                      <Input
                        variant="unstyled"
                        placeholder={
                          selectedInstallSequence.length === 0
                            ? "Install Sequence"
                            : ""
                        }
                        size="sm"
                        flex={1}
                        minW="80px"
                        value={installSequenceInput}
                        onChange={(e) => setInstallSequenceInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (
                            e.key === "Backspace" &&
                            installSequenceInput === "" &&
                            selectedInstallSequence.length > 0
                          ) {
                            updateFilter(
                              "selectedInstallSequence",
                              selectedInstallSequence.slice(0, -1)
                            );
                          }
                          if (e.key === "Enter" || e.key === ",") {
                            e.preventDefault();
                            const raw = installSequenceInput
                              .split(",")
                              .map((s) => s.trim())
                              .filter(Boolean);
                            const valid = raw.filter((s) => {
                              const n = Number(s);
                              return Number.isInteger(n) && n >= 1 && n <= 500;
                            });
                            if (valid.length > 0) {
                              const deduped = [
                                ...new Set([
                                  ...selectedInstallSequence,
                                  ...valid,
                                ]),
                              ];
                              updateFilter("selectedInstallSequence", deduped);
                            }
                            setInstallSequenceInput("");
                          }
                        }}
                      />
                    </Flex>
                    <Text fontSize="xs" color="gray.500" mt={1} px={2}>
                      Supports multiple entries separated by comma (1-500)
                    </Text>
                  </GridItem>
                </>
              )}
            </SimpleGrid>
          </Flex>
          <Flex direction={"column"} marginLeft={4}>
            <Box ref={ref}>
              <Heading size={"xs"} marginBottom={2}>
                Date Range
              </Heading>
              <Input
                borderRadius={16}
                onClick={() => {
                  //setInternalRanges(ranges);
                  setDatePickerVisible(!isDatePickerVisible);
                }}
                style={{
                  backgroundColor: "#fff",
                  textAlign: "center",
                  width: differentDates * 80 + 40,
                }}
                fontSize="sm"
                px={"8px"}
                defaultValue={formattedDate}
                value={formattedDate}
              />

              {isDatePickerVisible && (
                <Box
                  style={{
                    position: "absolute",
                    right: 20,
                    zIndex: 200,
                    top: 50,
                    boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                  }}
                >
                  <DateRangePicker
                    ranges={[internalRanges]}
                    onChange={(newRanges: any) => {
                      updateFilter("internalRanges", {
                        endDate: newRanges.selection.endDate,
                        key: newRanges.selection.key,
                        startDate: newRanges.selection.startDate,
                      });
                    }}
                  />
                </Box>
              )}
            </Box>
            {isMain && (
              <Box ref={userDatePickerRef}>
                <Heading mt={4} size={"xs"} marginBottom={2}>
                  User Range
                </Heading>
                <Input
                  borderRadius={16}
                  onClick={() => {
                    setUserDatePickerVisible(!isUserDatePickerVisible);
                  }}
                  style={{
                    backgroundColor: "white",
                    textAlign: "center",
                    width: differentUserDates * 80 + 40,
                  }}
                  fontSize="sm"
                  px={"8px"}
                  value={formattedUserDate ? formattedUserDate : ""}
                  placeholder={"Select a date"}
                />

                {(userInternalRanges.startDate ||
                  userInternalRanges.startDate) && (
                  <Button
                    onClick={() => {
                      updateFilter("userInternalRanges", {
                        startDate: null,
                        endDate: null,
                        key: "selection",
                      });
                    }}
                    ml={1}
                  >
                    <CloseIcon width={3} color="red" />
                  </Button>
                )}

                {isUserDatePickerVisible && (
                  <Box
                    style={{
                      position: "absolute",
                      right: 20,
                      zIndex: 200,
                      top: 70,
                      boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                    }}
                  >
                    <DateRangePicker
                      ranges={[
                        {
                          startDate: userInternalRanges.startDate
                            ? new Date(userInternalRanges.startDate)
                            : new Date(),
                          endDate: userInternalRanges.endDate
                            ? new Date(userInternalRanges.endDate)
                            : new Date(),
                          key: userInternalRanges.key,
                        },
                      ]}
                      onChange={(newRanges: any) => {
                        updateFilter("userInternalRanges", {
                          startDate: newRanges.selection.startDate,
                          endDate: newRanges.selection.endDate,
                          key: newRanges.selection.key,
                        });
                      }}
                    />
                  </Box>
                )}
                <CSVLink
                  data={csvData}
                  headers={csvHeaders}
                  filename={csvFileName}
                  style={{
                    pointerEvents: csvData.length === 0 ? "none" : "auto",
                  }}
                >
                  <GamelightButton
                    disabled={csvData.length === 0}
                    size="xs"
                    mt={2}
                    fontSize="10px"
                    height="24px"
                    px={2}
                    variant="outline"
                  >
                    Export CSV
                  </GamelightButton>
                </CSVLink>
              </Box>
            )}
          </Flex>
        </Flex>
      </Flex>
      <Heading mt={4} size={"md"}>
        Breakdowns
      </Heading>
      <Flex mt={2} gap={2}>
        <SimpleGrid flex={1} columns={4} gap={4}>
          <Flex
            backgroundColor={"white"}
            borderRadius={8}
            flexDir="row"
            flex={1}
          >
            <Flex flex={1}>
              <Select
                value={periodicity}
                isMulti={false}
                placeholder={"Select a Date Range"}
                onChange={(v) => {
                  updateFilter("periodicity", v);
                }}
                isClearable={false}
                chakraStyles={{
                  container(provided) {
                    return {
                      ...provided,
                      flex: 1,
                    };
                  },
                }}
                options={periodicityOptions}
              />
            </Flex>
          </Flex>
          {breakdowns.map((breakdown, index) => (
            <Flex
              backgroundColor={"white"}
              borderRadius={8}
              key={index}
              flexDir="row"
              flex={1}
            >
              <Flex flex={1}>
                <Select
                  value={breakdown}
                  isMulti={false}
                  placeholder={"Select Breakdown"}
                  onChange={(v) => addBreakdown(index, v)}
                  isClearable={false}
                  chakraStyles={{
                    container(provided) {
                      return {
                        ...provided,
                        flex: 1,
                      };
                    },
                  }}
                  //@ts-ignore
                  options={
                    breakdowns.length > 1
                      ? [
                          ...breakdownOptions.filter(
                            (opt) =>
                              !breakdowns.find((brk) => brk.value === opt.value)
                          ),
                        ]
                      : breakdownOptions
                  }
                />
              </Flex>
              {breakdown?.label && (
                <IconButton
                  display={"flex"}
                  aria-label="remove breakdown"
                  borderRadius={8}
                  backgroundColor="white"
                  icon={<IoRemoveCircle color="red.600" />}
                  onClick={() => {
                    if (breakdowns.length === 1) {
                      updateFilter("breakdowns", [null]);
                      return;
                    }

                    const isLastBreakdown = availableBreakdowns.length === 0;
                    const updatedBreakdowns = breakdowns.filter(
                      (_, idx) => idx !== index
                    );

                    if (isLastBreakdown) {
                      return updateFilter("breakdowns", [
                        ...updatedBreakdowns,
                        { label: "", value: "" },
                      ]);
                    }

                    updateFilter("breakdowns", updatedBreakdowns);
                  }}
                />
              )}
            </Flex>
          ))}
        </SimpleGrid>
        <Button
          bgColor={"brand.500"}
          color="white"
          _hover={{
            backgroundColor: "brand.600",
          }}
          onClick={handleApply}
        >
          Apply
        </Button>
      </Flex>
      {isMain && (
        <Flex mt={4} gap={4}>
          <SimpleGrid
            background={"#fff"}
            px="1rem"
            py="0.5rem"
            display={"flex"}
            gap={2}
            borderRadius={8}
          >
            <Text>Show CF</Text>
            <Checkbox
              isChecked={showCoinsEarned}
              onChange={(e) => {
                setShowCoinsEarned(e.target.checked);
                updateColumnsForCoinsEarned(e.target.checked);
              }}
            />
          </SimpleGrid>
          <SimpleGrid
            background={"#fff"}
            px="1rem"
            py="0.5rem"
            display={"flex"}
            gap={2}
            borderRadius={8}
          >
            <Text>Show ARPU</Text>
            <Checkbox
              isChecked={showArpu}
              onChange={(e) => setShowArpu(e.target.checked)}
            />
          </SimpleGrid>
          <SimpleGrid
            background={"#fff"}
            display={"flex"}
            gap={2}
            borderRadius={8}
          >
            <Select<CoinFilterOption, false>
              value={
                selectedCoinFilter
                  ? {
                      label:
                        coinFilterOptions.find(
                          (opt) => opt.value === selectedCoinFilter
                        )?.label || selectedCoinFilter,
                      value: selectedCoinFilter,
                    }
                  : null
              }
              isMulti={false}
              placeholder={"Coin Filter Type"}
              onChange={(v) => {
                updateFilter("selectedCoinFilter", v?.value || null);
              }}
              isClearable={true}
              options={coinFilterOptions}
              chakraStyles={{
                container(provided) {
                  return {
                    ...provided,
                    width: 250,
                  };
                },
              }}
            />
          </SimpleGrid>
        </Flex>
      )}

      <ComplexTable
        isLoading={reportRequestState.isLoading}
        columns={tableColumns}
        tableData={report ? report.rows : []}
        showArpu={showArpu}
      />
    </Box>
  );
}
